// external
import { useEffect, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import uniqid from "uniqid";

// components
import CustomSelect from "components/partials/custom-select/custom-select";
import CustomButton from "components/partials/custom-button/custom-button";
import ModelCard from "components/ModelCard";
import Loader from "components/partials/loader/loader";

// api
import apiRoot from "api/apiRoot";

// state managing/security
import { AuthContext } from "context/AuthContext";
import { ClientContext } from "context/ClientContext";
import { FaoBuilderContext } from "context/FaoBuilderContext";

// assets
import Icons from "assets/icons";

// css/styling
import { STYLES, mergeStyles } from "./constants/styles.constants";

const selectStyle = {
  control: mergeStyles(STYLES.select.control, () => ({
    width: "100%",
  })),
  option: STYLES.select.option,
  menuPortal: STYLES.select.menuPortal,
};
function FaoClone() {
  const navigate = useNavigate();
  const { currentClientData } = useContext(ClientContext);
  const { fetchModels, setModelConfig } = useContext(FaoBuilderContext);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCloning, setIsCloning] = useState(false);
  const [modelOptions, setModelOptions] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const { userName } = useContext(AuthContext);
  useEffect(() => {
    if (currentClientData?.schoolKey) {
      fetchModels(setIsLoading, setError, setModelOptions);
    }
  }, [currentClientData?.schoolKey, fetchModels]);

  const handleCloneModel = async () => {
    setIsCloning(true);
    const newModelConfigs = {};
    Object.entries(selectedModel.configs).forEach(([key, value]) => {
      // regimeConfig is a special case, it needs to be cloned as regimeParams
      if (key === "regimeConfig") {
        newModelConfigs[key] = newModelConfigs["regimeParams"];
      } else {
        newModelConfigs[key] = value !== "" ? uniqid() : value;
      }
    });
    const requestBody = {
      ...selectedModel,
      id: uniqid(),
      authorName: userName,
      aidResults: null,
      configs: newModelConfigs,
    };
    const response = await apiRoot.post(
      "/api/fao-model/clone",
      requestBody, // This is the request body
      {
        params: { sourceModelId: selectedModel.id }, // This is for query parameters
      }
    );
    setModelConfig(response.data);
    navigate(`../fao-builder/${response.data.id}/metadata`);
  };
  const handleModelSelect = (selected) => {
    setSelectedModel(modelOptions.find((item) => item.id === selected.value));
  };

  const CustomOption = (props) => {
    const { innerProps, data, getStyles, isSelected } = props;
    const isPublished = data.status === "published";
    return (
      <div
        {...innerProps}
        className="hover:bg-blue-03/30"
        style={getStyles("option", props)}
      >
        <div className="flex justify-between">
          <div className="font-semibold">{data.label}</div>
          {isPublished && (
            <div className="text-sm font-semibold text-green-500 transition-all">
              Published
            </div>
          )}
        </div>
        <div
          className={classNames("w-max rounded-md", {
            "text-white": isSelected,
          })}
        >
          {data.value}
        </div>
      </div>
    );
  };

  return (
    <div className="">
      <div className="flex flex-col gap-4">
        <h1 className="text-2xl font-bold text-blue-03">
          Clone Existing Model
        </h1>

        <CustomSelect
          options={modelOptions.map((item) => ({
            value: item.id,
            label: item.versionName,
            status: item.status,
          }))}
          disabled={isLoading}
          isLoading={isLoading}
          onChange={handleModelSelect}
          extraStyles={() => selectStyle}
          components={{ Option: CustomOption }}
        />

        {selectedModel && (
          <div className="w-full">
            <ModelCard model={selectedModel} disabled={true} />
          </div>
        )}

        {isLoading ? (
          <Loader />
        ) : (
          <CustomButton
            variant="primary"
            onClick={handleCloneModel}
            customClass="w-full"
          >
            {isCloning ? (
              <>
                <Icons.Spinner customClass="h-full w-4 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-900" />{" "}
                Cloning...{" "}
              </>
            ) : (
              "Clone Model"
            )}
          </CustomButton>
        )}
      </div>
    </div>
  );
}

export default FaoClone;
